import React, { useState, useRef, useEffect } from 'react';
import { LayerWithVisualization, LayerType } from '../../types/modelBuilder';
import { useModelBuilder } from '../../context/ModelBuilderContext';
import { LAYER_TEMPLATES, createLayerFromTemplate, calculateOutputShape, validateConnection } from '../../utils/layerUtils';
import TensorVisualizer from './TensorVisualizer';
import '../../styles/modelBuilder.css';

interface DraggedLayer {
  template: typeof LAYER_TEMPLATES[LayerType];
  startPos: { x: number; y: number };
}

export const ModelCanvas: React.FC = () => {
  const {
    layers,
    selectedLayerId,
    selectLayer,
    addLayer,
    updateLayer,
    removeLayer,
    connectLayers,
    disconnectLayers,
  } = useModelBuilder();

  const canvasRef = useRef<HTMLDivElement>(null);
  const [draggedTemplate, setDraggedTemplate] = useState<DraggedLayer | null>(null);
  const [connectionMode, setConnectionMode] = useState(false);
  const [connectionStart, setConnectionStart] = useState<string | null>(null);
  const [zoom, setZoom] = useState(1);
  const [pan, setPan] = useState({ x: 0, y: 0 });
  const [isPanning, setIsPanning] = useState(false);
  const [panStart, setPanStart] = useState({ x: 0, y: 0 });
  const contentsRef = useRef<HTMLDivElement>(null);

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'copy';
  };

  const handleWheel = (e: React.WheelEvent) => {
    if (!canvasRef.current) return;
    e.preventDefault();

    const zoomSpeed = 0.1;
    const newZoom = Math.max(0.3, Math.min(3, zoom + (e.deltaY > 0 ? -zoomSpeed : zoomSpeed)));
    setZoom(newZoom);
  };

  const handleCanvasMouseDown = (e: React.MouseEvent) => {
    // Only pan on left mouse button (button 0)
    if (e.button === 0) {
      setIsPanning(true);
      setPanStart({ x: e.clientX - pan.x, y: e.clientY - pan.y });
    }
  };

  const handleCanvasMouseMove = (e: React.MouseEvent) => {
    if (isPanning) {
      setPan({
        x: e.clientX - panStart.x,
        y: e.clientY - panStart.y,
      });
    }
  };

  const handleCanvasMouseUp = () => {
    setIsPanning(false);
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    const layerType = e.dataTransfer.getData('layerType') as LayerType;
    const template = LAYER_TEMPLATES[layerType];

    if (!template || !canvasRef.current) return;

    const rect = canvasRef.current.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const y = e.clientY - rect.top;

    // Get input shape from previous layer if exists
    let inputShape: number[] = [];
    let inputs: string[] = ['input'];
    if (layers.length > 0) {
      const lastLayer = layers[layers.length - 1];
      inputShape = lastLayer.outputShape || [];
      inputs = [lastLayer.id];
    }

    const newLayer = createLayerFromTemplate(template, { x, y, z: 0 }, inputShape, inputs);

    // Calculate output shape
    newLayer.inputShape = inputShape;
    newLayer.outputShape = calculateOutputShape(
      inputShape,
      newLayer.type,
      newLayer
    );

    addLayer(newLayer);
    selectLayer(newLayer.id);
  };

  const handleLayerClick = (layerId: string) => {
    if (connectionMode) {
      if (connectionStart && connectionStart !== layerId) {
        const fromLayer = layers.find(l => l.id === connectionStart);
        const toLayer = layers.find(l => l.id === layerId);

        if (fromLayer && toLayer) {
          const validation = validateConnection(fromLayer, toLayer);
          if (validation.valid) {
            connectLayers(connectionStart, layerId);
          } else {
            alert(validation.error || 'Cannot connect these layers');
          }
        }
        setConnectionMode(false);
        setConnectionStart(null);
      } else {
        setConnectionStart(layerId);
      }
    } else {
      selectLayer(selectedLayerId === layerId ? null : layerId);
    }
  };

  const handleLayerContextMenu = (e: React.MouseEvent, layerId: string) => {
    e.preventDefault();
    const layer = layers.find(l => l.id === layerId);
    if (!layer) return;

    const menu = document.createElement('div');
    menu.className = 'context-menu';
    menu.style.position = 'fixed';
    menu.style.left = e.clientX + 'px';
    menu.style.top = e.clientY + 'px';
    menu.innerHTML = `
      <button data-action="delete">🗑️ Delete</button>
      <button data-action="duplicate">📋 Duplicate</button>
      <button data-action="connect">🔗 Connect</button>
    `;

    menu.addEventListener('click', (ev) => {
      const action = (ev.target as HTMLElement).getAttribute('data-action');
      if (action === 'delete') removeLayer(layerId);
      if (action === 'duplicate') {
        // TODO: implement duplicate
      }
      if (action === 'connect') {
        setConnectionMode(true);
        setConnectionStart(layerId);
      }
      document.body.removeChild(menu);
    });

    document.body.appendChild(menu);
    setTimeout(() => {
      if (document.body.contains(menu)) document.body.removeChild(menu);
    }, 5000);
  };

  const getLayerPosition = (layer: LayerWithVisualization) => {
    return {
      left: `${layer.position.x}px`,
      top: `${layer.position.y}px`,
    };
  };

  const isLayerConnected = (fromId: string, toId: string) => {
    const toLayer = layers.find(l => l.id === toId);
    return toLayer?.inputs.includes(fromId) || false;
  };

  return (
    <div className="model-canvas">
      <div className="canvas-toolbar">
        <button
          className={`mode-btn ${connectionMode ? 'active' : ''}`}
          onClick={() => {
            setConnectionMode(!connectionMode);
            setConnectionStart(null);
          }}
          title="Connect layers mode"
        >
          🔗
        </button>
        <div className="toolbar-spacer" />
        <span className="layer-count">{layers.length} layers</span>
        <span className="zoom-indicator">🔍 {(zoom * 100).toFixed(0)}%</span>
      </div>

      <div
        ref={canvasRef}
        className="canvas-area"
        onDragOver={handleDragOver}
        onDrop={handleDrop}
        onWheel={handleWheel}
        onMouseDown={handleCanvasMouseDown}
        onMouseMove={handleCanvasMouseMove}
        onMouseUp={handleCanvasMouseUp}
        onMouseLeave={handleCanvasMouseUp}
        style={{ cursor: isPanning ? 'grabbing' : 'default' }}
      >
        <div
          ref={contentsRef}
          style={{
            transform: `translate(${pan.x}px, ${pan.y}px) scale(${zoom})`,
            transformOrigin: '0 0',
            transition: zoom === 1 && !isPanning ? 'none' : 'transform 0.1s ease-out',
          }}
        >
        {layers.length === 0 ? (
          <div className="empty-canvas">
            <div className="empty-icon">🎨</div>
            <p>Drag layers here to build your neural network</p>
          </div>
        ) : (
          <>
            {/* Connection lines */}
            <svg className="connections-svg">
              {layers.map((toLayer) =>
                (toLayer.inputs || []).map((fromId) => {
                  // Handle "input" as special case
                  if (fromId === 'input') {
                    const x1 = 0;
                    const y1 = toLayer.position.y + 50;
                    const x2 = toLayer.position.x;
                    const y2 = toLayer.position.y + 50;

                    return (
                      <line
                        key={`input-${toLayer.id}`}
                        x1={x1}
                        y1={y1}
                        x2={x2}
                        y2={y2}
                        className="connection-line"
                        strokeWidth="2"
                        stroke="#8B5CF6"
                        markerEnd="url(#arrowhead)"
                      />
                    );
                  }

                  const fromLayer = layers.find(l => l.id === fromId);
                  if (!fromLayer) return null;

                  const x1 = fromLayer.position.x + 100;
                  const y1 = fromLayer.position.y + 50;
                  const x2 = toLayer.position.x;
                  const y2 = toLayer.position.y + 50;

                  return (
                    <line
                      key={`${fromId}-${toLayer.id}`}
                      x1={x1}
                      y1={y1}
                      x2={x2}
                      y2={y2}
                      className="connection-line"
                      strokeWidth="2"
                      stroke="#8B5CF6"
                      markerEnd="url(#arrowhead)"
                    />
                  );
                })
              )}
              <defs>
                <marker
                  id="arrowhead"
                  markerWidth="10"
                  markerHeight="10"
                  refX="9"
                  refY="3"
                  orient="auto"
                >
                  <polygon points="0 0, 10 3, 0 6" fill="#8B5CF6" />
                </marker>
              </defs>
            </svg>

            {/* Layer nodes */}
            {layers.map((layer) => (
              <div
                key={layer.id}
                className={`layer-node ${selectedLayerId === layer.id ? 'selected' : ''} ${
                  connectionMode && (layer.id === connectionStart ? 'connection-start' : '')
                }`}
                style={getLayerPosition(layer)}
                onClick={() => handleLayerClick(layer.id)}
                onContextMenu={(e) => handleLayerContextMenu(e, layer.id)}
              >
                <div className="layer-content">
                  <div className="layer-icon">{LAYER_TEMPLATES[layer.type].icon}</div>
                  <div className="layer-info">
                    <div className="layer-name">{layer.name}</div>
                    <div className="layer-type-small">{layer.type}</div>
                  </div>
                </div>
                <div className="layer-shapes">
                  {(layer.inputShape || []).length > 0 && (
                    <TensorVisualizer shape={layer.inputShape || []} color="#10B981" size="small" />
                  )}
                  <span className="arrow">→</span>
                  <TensorVisualizer shape={layer.outputShape || []} color="#3B82F6" size="small" />
                </div>
              </div>
            ))}
          </>
        )}
        </div>
      </div>
    </div>
  );
};

export default ModelCanvas;
